import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useApi } from '../hooks/useApi';
import { 
  Users, 
  MessageCircle, 
  Calendar, 
  Car, 
  TrendingUp, 
  Mail, 
  Phone,
  MapPin,
  Clock,
  Eye,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const AdminPanel = () => {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { callApi } = useApi();

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      // Charger les statistiques
      const statsData = await callApi('/stats');
      setStats(statsData);

      // Charger les contacts
      const contactsData = await callApi('/contact');
      setContacts(contactsData);

      // Charger les devis
      const quotesData = await callApi('/quote');
      setQuotes(quotesData);

      // Charger les réservations
      const bookingsData = await callApi('/booking');
      setBookings(bookingsData);

    } catch (error) {
      console.error('Erreur de chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'new': 'bg-blue-500',
      'contacted': 'bg-yellow-500',
      'quoted': 'bg-orange-500',
      'booked': 'bg-green-500',
      'completed': 'bg-gray-500',
      'pending': 'bg-blue-500',
      'confirmed': 'bg-green-500',
      'cancelled': 'bg-red-500'
    };

    return (
      <Badge className={`${statusColors[status] || 'bg-gray-500'} text-white`}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Chargement du panneau d'administration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Panneau d'Administration Luxor Limousine
          </h1>
          <p className="text-gray-600">
            Gérez vos contacts, réservations et statistiques
          </p>
        </div>

        {/* Statistiques Rapides */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Users className="h-8 w-8 text-blue-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Contacts</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.operational.total_contacts}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <MessageCircle className="h-8 w-8 text-green-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Devis</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.operational.total_quotes}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <Calendar className="h-8 w-8 text-yellow-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Réservations</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.operational.total_bookings}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Taux Conversion</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.success_rate.contact_to_booking}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Onglets Principaux */}
        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="quotes">Devis ({quotes.length})</TabsTrigger>
            <TabsTrigger value="bookings">Réservations ({bookings.length})</TabsTrigger>
          </TabsList>

          {/* Onglet Contacts */}
          <TabsContent value="contacts" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="mr-2" />
                  Demandes de Contact Récentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contacts.slice(0, 10).map((contact) => (
                    <div key={contact.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold text-lg">{contact.name}</h3>
                          <div className="flex items-center text-sm text-gray-600 mt-1 space-x-4">
                            <span className="flex items-center">
                              <Mail className="w-4 h-4 mr-1" />
                              {contact.email}
                            </span>
                            <span className="flex items-center">
                              <Phone className="w-4 h-4 mr-1" />
                              {contact.phone}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(contact.status)}
                          <p className="text-sm text-gray-500 mt-1">
                            {formatDate(contact.created_at)}
                          </p>
                        </div>
                      </div>
                      
                      {contact.service && (
                        <p className="text-sm text-gray-700 mb-2">
                          <strong>Service:</strong> {contact.service}
                        </p>
                      )}
                      
                      {contact.message && (
                        <p className="text-sm text-gray-700 bg-gray-100 p-3 rounded">
                          {contact.message}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-sm text-gray-500">
                          Langue: {contact.language?.toUpperCase()}
                        </span>
                        <Button size="sm" variant="outline">
                          <Eye className="w-4 h-4 mr-1" />
                          Voir Détails
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onglet Devis */}
          <TabsContent value="quotes" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageCircle className="mr-2" />
                  Devis Récents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {quotes.slice(0, 10).map((quote) => (
                    <div key={quote.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="font-semibold text-lg">{quote.name}</h3>
                          <p className="text-sm text-gray-600">{quote.email}</p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(quote.status)}
                          <p className="text-xl font-bold text-green-600 mt-1">
                            {quote.estimated_price} CHF
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p><strong>Service:</strong> {quote.service_type}</p>
                          <p><strong>Passagers:</strong> {quote.passenger_count}</p>
                        </div>
                        <div>
                          <p><strong>Départ:</strong> {quote.pickup_location || 'Non spécifié'}</p>
                          <p><strong>Destination:</strong> {quote.destination || 'Non spécifié'}</p>
                        </div>
                      </div>
                      
                      {quote.additional_info && (
                        <p className="text-sm text-gray-700 bg-gray-100 p-3 rounded mt-3">
                          {quote.additional_info}
                        </p>
                      )}
                      
                      <div className="flex justify-between items-center mt-3">
                        <span className="text-sm text-gray-500">
                          {formatDate(quote.created_at)}
                        </span>
                        <Button size="sm" variant="outline">
                          Convertir en Réservation
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Onglet Réservations */}
          <TabsContent value="bookings" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="mr-2" />
                  Réservations Récentes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {bookings.slice(0, 10).map((booking) => (
                    <div key={booking.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{booking.contact_info.name}</h3>
                          <p className="text-lg font-bold text-blue-600">{booking.confirmation_number}</p>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(booking.status)}
                          <p className="text-xl font-bold text-green-600 mt-1">
                            {booking.final_price} CHF
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-1">
                          <p><strong>Service:</strong> {booking.service_details.service_type}</p>
                          <p><strong>Véhicule:</strong> {booking.service_details.vehicle_category}</p>
                          <p><strong>Passagers:</strong> {booking.service_details.passenger_count}</p>
                        </div>
                        <div className="space-y-1">
                          <p className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            <strong>Départ:</strong> {booking.service_details.pickup_location}
                          </p>
                          <p className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            <strong>Destination:</strong> {booking.service_details.destination}
                          </p>
                          <p className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            <strong>Date:</strong> {formatDate(booking.service_details.pickup_datetime)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600 mt-3 space-x-4">
                        <span className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {booking.contact_info.email}
                        </span>
                        <span className="flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          {booking.contact_info.phone}
                        </span>
                      </div>
                      
                      {booking.service_details.special_requests && (
                        <p className="text-sm text-gray-700 bg-gray-100 p-3 rounded mt-3">
                          <strong>Demandes spéciales:</strong> {booking.service_details.special_requests}
                        </p>
                      )}
                      
                      <div className="flex justify-between items-center mt-4 pt-3 border-t">
                        <span className="text-sm text-gray-500">
                          Réservé le {formatDate(booking.created_at)}
                        </span>
                        <div className="space-x-2">
                          <Button size="sm" variant="outline">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Confirmer
                          </Button>
                          <Button size="sm" variant="outline">
                            <Eye className="w-4 h-4 mr-1" />
                            Détails
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;